import FiltersBar from '../../components/FiltersBar';
import { Box, Stack, Drawer, Autocomplete, TextField, Checkbox, IconButton, Typography, Card, CardContent, } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LinearProgress from "@mui/material/LinearProgress";
import { useTranslation } from 'react-i18next';
import { ApiContext } from '../../App';
import OccupancyHeatmap from './components/OccupancyHeatmap';
import React, { useMemo } from 'react';
import InputLineChart from '../../components/InputLineChart';
import OccupancySummaryCard from './components/OccupancySummaryCard';
import OccupancyLineChart from './components/OccupancyLineChart';
import OccupancyFiltersCard from './components/OccupancyFiltersCard';
import { filterDataToSelectedIntervals, filterInBasedOnSystemAndLocation } from './utils';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function Occupancy({ facility, clientInfo }) {

  const { t } = useTranslation();
  const drawerInputWidth = 500;

  const { occupancyApi } = React.useContext(ApiContext);
  const localTimeZone = clientInfo?.timezone;

  var earlierDate = new Date();
  earlierDate.setDate(earlierDate.getDate() - 1);
  const [fromDate, setFromDate] = React.useState(earlierDate);
  const [toDate, setToDate] = React.useState(new Date());
  const [timeAggType, setTimeAggType] = React.useState("h");

  const [isLoading, setIsLoading] = React.useState(false);


  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = React.useState([0, 1, 2, 3, 4, 5, 6]);
  const [selectedHoursOfDay, setSelectedHoursOfDay] = React.useState(Array.from({ length: 24 }, (_, hour) => hour));

  const [filters, setFilters] = React.useState({ system: [], location: [] });
  const [selectedFilters, setSelectedFilters] = React.useState({ system: [], location: [] });
  const [appliedFilters, setAppliedFilters] = React.useState({ system: [], location: [] });
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);

  const [occupancyData, setOccupancyData] = React.useState({ "motion_event": { "points_data": [], "timestamps": [] }, "occupancy_event": { "points_data": [], "timestamps": [] }, "occupancy_count": { "points_data": [], "timestamps": [] }, "people_in": { "points_data": [], "timestamps": [] }, "people_out": { "points_data": [], "timestamps": [] } });

  const filteredOccupancyData = useMemo(() => {
    if (appliedFilters.system.length === 0 && appliedFilters.location.length === 0) {
      return occupancyData;
    } else {
      return filterInBasedOnSystemAndLocation(occupancyData, appliedFilters.system, appliedFilters.location);
    }
  }, [appliedFilters, occupancyData])

  const handleTimeAggTypeChange = (event, newValue) => {
    setTimeAggType(newValue);
  }
  const handleAPIError = () => {
    // simple fallback alert
    window.alert(t("Problem fetching data."))
  }

  const optionsOverrides = useMemo(() => {
    const allLocations = [...new Set([...(filteredOccupancyData.motion_event.points_data ?? []).map(d => d.metadata.location),
    ...(filteredOccupancyData.people_in.points_data ?? []).map(d => d.metadata.location),
    ...(filteredOccupancyData.people_out.points_data ?? []).map(d => d.metadata.location),
    ]),
    ];
    const maxSuppliedLabelLength = Math.max(...allLocations.map(label => label.length), 3);
    return {
      yAxis: {
        axisLabel: {
          formatter: (value) => {
            const stringValue = String(value);
            return stringValue.padStart(maxSuppliedLabelLength, ' ');
          }
        },

      },
      textStyle: {
        fontSize: 12,
        fontFamily: "monospace"
      },
      grid: {
        containLabel: false,
        left: maxSuppliedLabelLength * 8 + 20,
        bottom: 90
      }
    }
  }, [filteredOccupancyData])

  React.useEffect(() => {
    const allPoints = Object.values(occupancyData || {}).flatMap(
      (entry) => entry?.points_data ?? []
    );
    const locations = [...new Set(allPoints.map((d) => d.metadata.location))].sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    setFilters((val) => ({ ...val, location: locations }));
    const systems = [...new Set(allPoints.map((d) => d.metadata.system))].sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    setFilters((val) => ({ ...val, system: systems }));

  }, [occupancyData])

  React.useEffect(() => {
    // if the currently applied filters are no longer valid based on the new data, reset them
    setAppliedFilters((val) => {
      const newLocationFilters = val.location.filter((location) => filters.location.includes(location));
      const newSystemFilters = val.system.filter((system) => filters.system.includes(system));
      return { location: newLocationFilters, system: newSystemFilters };
    })
    setSelectedFilters((val) => {
      const newLocationFilters = val.location.filter((location) => filters.location.includes(location));
      const newSystemFilters = val.system.filter((system) => filters.system.includes(system));
      return { location: newLocationFilters, system: newSystemFilters };
    })
  }, [filters])


  React.useEffect(() => {
    setIsLoading(true);
    occupancyApi.getOccupancyTrends(
      facility,
      fromDate,
      toDate,
      (data) => {
        setOccupancyData(data);
        setIsLoading(false);
      },
      () => { handleAPIError(); setIsLoading(false); },
      timeAggType,
      [],
      []
    )
  }, [fromDate, toDate, facility, timeAggType, occupancyApi])


  const timeFilteredOccupancyEventData = useMemo(() => {
    return filterDataToSelectedIntervals(filteredOccupancyData.occupancy_event, selectedHoursOfDay, selectedDaysOfWeek);
  }, [filteredOccupancyData, selectedHoursOfDay, selectedDaysOfWeek]);

  const closeFilterDrawer = () => {
    setFilterDrawerOpen(false);
    setAppliedFilters(selectedFilters);
  }

  return (
    <Box marginBottom={5} >
      <FiltersBar
        showKeySelector={false}
        showSave={false}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={(newDate) => setFromDate(newDate)}
        onToDateChange={(newDate) => setToDate(newDate)}
        timeAggType={timeAggType}
        onTimeAggChange={handleTimeAggTypeChange}
        onOpenFilterDrawer={() => setFilterDrawerOpen(true)}
        showTimeAggregationFilter={false}
      />
      {isLoading && <Box sx={{ width: '100%' }}><LinearProgress /></Box>}
      <Box sx={{
        backgroundColor: (theme) =>
          theme.palette.mode === "light"
            ? theme.palette.grey[100]
            : theme.palette.grey[900],
        p: 3,
        minHeight: "calc(100vh - 65px)"
      }}>
        <Stack>
          <Stack>
            <OccupancyFiltersCard selectedHours={selectedHoursOfDay} setSelectedHours={setSelectedHoursOfDay} selectedDays={selectedDaysOfWeek} setSelectedDays={setSelectedDaysOfWeek}
              downloadableData={filteredOccupancyData}
            />
          </Stack>
          <Stack key="summary" >
            {
              filteredOccupancyData.occupancy_event.points_data.length > 0 &&
              <OccupancySummaryCard occupancyEventData={timeFilteredOccupancyEventData} />
            }
          </Stack>
          {
            filteredOccupancyData.occupancy_event.points_data.length > 0 &&
            <Stack key="occupied_line_chart">
              <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                <CardContent>
                  <Typography padding={1} align="left" component="h2" variant="h6" color="primary" gutterBottom>
                    {t("Occupied")}
                  </Typography>
                  <OccupancyLineChart
                    occupancyEventData={filteredOccupancyData?.occupancy_event}
                    timeAggType={timeAggType}
                    localTimeZone={localTimeZone}
                  />
                </CardContent>
              </Card>
            </Stack>
          }
          {
            filteredOccupancyData.occupancy_event.points_data.length > 0 &&
            <Stack key="occupied_heatmap">
              <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                <CardContent>
                  <Typography padding={1} align="left" component="h2" variant="h6" color="primary" gutterBottom>
                    {t("Occupied Heatmap")}
                  </Typography>
                  <OccupancyHeatmap
                    occupancyEventData={timeFilteredOccupancyEventData}
                    optionsOverrides={optionsOverrides}
                  />
                </CardContent>
              </Card>
            </Stack>
          }
          {filteredOccupancyData?.occupancy_count?.points_data?.length > 0 && (
            <Stack key="occupancy_count">
              <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                <CardContent>
                  <Typography padding={1} align="center" component="h2" variant="h6" color="primary" gutterBottom>
                    {t("Occupancy count")}
                  </Typography>
                  <InputLineChart
                    graphType={'OCCUPANCY_COUNT'}
                    data={filteredOccupancyData?.occupancy_count.points_data ?? []}
                    timestamps={filteredOccupancyData?.occupancy_count.timestamps ?? []}
                    timeAggType={timeAggType}
                    localTimeZone={localTimeZone}
                    optionsOverrides={optionsOverrides}
                  />
                </CardContent>
              </Card>
            </Stack>
          )}
          {occupancyData.people_in.points_data.length > 0 && occupancyData.people_out.points_data.length > 0 && (<>
            <Stack key="people_in">
              <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                <CardContent>
                  <Typography padding={1} align="center" component="h2" variant="h6" color="primary" gutterBottom>
                    {t("People In")}
                  </Typography>
                  <InputLineChart
                    graphType={'PEOPLE_IN'}
                    data={filteredOccupancyData?.people_in.points_data ?? []}
                    timestamps={filteredOccupancyData?.people_in.timestamps ?? []}
                    timeAggType={timeAggType}
                    localTimeZone={localTimeZone}
                    optionsOverrides={optionsOverrides}
                  />
                </CardContent>
              </Card>
            </Stack>
            <Stack key="people_out">
              <Card sx={{ mx: { xs: 2, md: 3 }, my: 2 }}>
                <CardContent>
                  <Typography padding={1} align="center" component="h2" variant="h6" color="primary" gutterBottom>
                    {t("People Out")}
                  </Typography>
                  <InputLineChart
                    graphType={'PEOPLE_OUT'}
                    data={filteredOccupancyData?.people_out.points_data ?? []}
                    timestamps={filteredOccupancyData?.people_out.timestamps ?? []}
                    timeAggType={timeAggType}
                    localTimeZone={localTimeZone}
                    optionsOverrides={optionsOverrides}
                  />
                </CardContent>
              </Card>
            </Stack>
          </>)}
        </Stack>


        <Drawer anchor="right" open={filterDrawerOpen} onClose={() => closeFilterDrawer()}>
          <Stack spacing={3} padding={2}>
            <Typography variant="h6" color="primary">{t("Filter")}</Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={5}
              value={selectedFilters.location}
              onChange={(event, newValue) => {
                setSelectedFilters((val) => ({ ...val, location: newValue }))
              }}
              options={filters.location}
              renderInput={(params) => <TextField {...params} label={t("Locations")} />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option}
                </li>
              )}
              sx={{ width: drawerInputWidth }}
            />
            <Autocomplete
              multiple
              limitTags={5}
              value={selectedFilters.system}
              onChange={(event, newValue) => {
                setSelectedFilters((val) => ({ ...val, system: newValue }))
              }}
              options={filters.system}
              renderInput={(params) => <TextField {...params} label={t("Systems")} />}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option}
                </li>
              )}
              sx={{ width: drawerInputWidth }}
            />

          </Stack>
        </Drawer>
      </Box>
    </Box>
  )

} 
